(() => {
  const initSection = (section) => {
    if (!section) return;

    const tabsWrapper = section.querySelector(".steps__items");
    const tabs = section.querySelectorAll(".steps__item");
    const contents = section.querySelectorAll(".steps__content");
    const bulletLine = section.querySelector(".steps__items-line");
    const EXTRA_SCROLL_MARGIN = 80; // additional top margin so headings stay visible
    const stepsRoot = section.querySelector(".steps");
    const ALIGN_TWEAK_WITH_PD = 16; // nudge for steps with extra top padding

    if (!tabsWrapper || !tabs.length || !contents.length) return;

    let isTabClicking = false;
    let scrollTimeout;

    // draw line
    const setBulletVariables = (activeTab) => {
      const topWrapperTop = tabsWrapper.getBoundingClientRect().top;
      const tabTop = activeTab.getBoundingClientRect().top;
      const bulletPosition = tabTop - topWrapperTop;
      const tabHeight = activeTab.offsetHeight;

      bulletLine.style.setProperty("--max_height_bullet", `${tabHeight}px`);
      bulletLine.style.setProperty("--pos_y_bullet", `${bulletPosition}px`);
    };

    // adjust sticky offset so the left steps block aligns with the image top
    const setStickyOffsetFor = (activeContent) => {
      if (!activeContent) return;
      // Prefer aligning to the visual top of the image inside the content
      let extraPx = 0;
      const img = activeContent.querySelector(".steps__content-image");
      if (img) {
        const contentRect = activeContent.getBoundingClientRect();
        const imgRect = img.getBoundingClientRect();
        const offsetWithin = imgRect.top - contentRect.top;
        if (!Number.isNaN(offsetWithin) && isFinite(offsetWithin)) {
          extraPx = Math.max(0, Math.round(offsetWithin));
        }
      }
      // Fallback: use computed padding-top if image not found
      if (extraPx === 0) {
        try {
          const style = window.getComputedStyle(activeContent);
          const pad = parseFloat(style.paddingTop || "0");
          if (!Number.isNaN(pad)) extraPx = Math.round(pad);
        } catch (_e) {
          // fallback to class-based heuristic
          extraPx = activeContent.classList.contains("steps__content--with-pd-top") ? 64 : 0;
        }
      }
      // add small visual tweak so the sidebar sits a bit lower than the anchor
      if (extraPx > 0) extraPx += ALIGN_TWEAK_WITH_PD;
      // set on the closest container that definitely wraps .steps__items
      const varHost = stepsRoot || section;
      varHost.style.setProperty("--steps-sticky-extra-top", `${Math.max(0, Math.round(extraPx))}px`);
      // also expose a generic scroll extra for CSS scroll-margin-top fallback
      varHost.style.setProperty("--steps-scroll-extra", `${EXTRA_SCROLL_MARGIN}px`);
    };

    // activate step by scroll page
    const activateStepByScroll = () => {
      if (isTabClicking) return;

      const middleOfScreen = window.innerHeight / 2 + window.scrollY;

      let closestElement = null;
      let closestDistance = Infinity;

      contents.forEach((content) => {
        const contentTop = content.getBoundingClientRect().top + window.scrollY;
        const contentBottom = contentTop + content.offsetHeight;

        const visibleHeight =
          Math.min(contentBottom, window.scrollY + window.innerHeight) -
          Math.max(contentTop, window.scrollY);

        if (visibleHeight < content.offsetHeight * 0.25) return;

        const contentCenter = contentTop + content.offsetHeight / 2;
        const distanceToMiddle = Math.abs(middleOfScreen - contentCenter);

        if (distanceToMiddle < closestDistance) {
          closestDistance = distanceToMiddle;
          closestElement = content;
        }
      });

      if (closestElement) {
        const activeId = closestElement.dataset.stepId;

        closestElement.classList.add("step-viewed");

        tabs.forEach((tab) => {
          tab.classList.remove("steps__item--active");

          if (tab.dataset.stepId === activeId) {
            tab.classList.add("steps__item--active");
            setBulletVariables(tab);
          }
        });
        setStickyOffsetFor(closestElement);
      }
    };

    // scroll to active step content after click
    const scrollToStep = (activeContent) => {
      // Prefer aligning the visual image block of the step, not the container
      const anchorEl =
        activeContent.querySelector(".steps__content-image") || activeContent;
      const elementPosition = anchorEl.getBoundingClientRect().top + window.scrollY;

      let offsetPosition = elementPosition - 16;

      const header = document.querySelector(".shopify-section-header");
      if (header) {
        const headerHeight = header.getBoundingClientRect().height;
        offsetPosition = offsetPosition - headerHeight;
      }

      // add small extra margin so we still see the step heading clearly
      offsetPosition = offsetPosition + EXTRA_SCROLL_MARGIN;

      if (activeContent.classList.contains("steps__content--with-pd-top")) {
        offsetPosition = offsetPosition + 32; // modest nudge for padded steps
      }

      isTabClicking = true;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });

      if (scrollTimeout) clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        isTabClicking = false;
      }, 800);
    };

    // activate step by click
    const activateStepByClick = (event) => {
      const activeTab = event.target.closest(".steps__item");
      if (!activeTab || activeTab.classList.contains("steps__item--active")) {
        return;
      }
      const activeId = activeTab.dataset.stepId;
      const activeContent = section.querySelector(
        `[data-step-id="${activeId}"].steps__content`
      );

      tabs.forEach((tab) => {
        tab.classList.remove("steps__item--active");
      });

      activeTab.classList.add("steps__item--active");
      if (activeContent) activeContent.classList.add("step-viewed");
      setBulletVariables(activeTab);
      setStickyOffsetFor(activeContent);
      if (activeContent) scrollToStep(activeContent);

      // reflect selected step in URL hash as #step-<handle>
      const handle = activeTab.getAttribute("data-step-handle");
      if (handle) {
        const newHash = `#step-${handle}`;
        if (window.history && window.history.replaceState) {
          const url = new URL(window.location.href);
          url.hash = newHash;
          window.history.replaceState(null, "", url.toString());
        } else {
          window.location.hash = newHash;
        }
      }
    };

    tabs.forEach((tab) => {
      tab.addEventListener("click", activateStepByClick);
      tab.addEventListener("keydown", (event) => {
        if (event.key === "Enter") {
          activateStepByClick(event);
        }
      });
    });

    // retry helper: ensure scroll executes after layout/images
    const scheduleRetryScroll = (contentEl) => {
      if (!contentEl) return;
      try {
        // immediate attempts
        requestAnimationFrame(() => scrollToStep(contentEl));
        setTimeout(() => scrollToStep(contentEl), 0);
        setTimeout(() => scrollToStep(contentEl), 250);
        setTimeout(() => scrollToStep(contentEl), 600);
        // after window load (images, fonts)
        window.addEventListener(
          "load",
          () => {
            scrollToStep(contentEl);
          },
          { once: true }
        );
      } catch (e) {
        // no-op
      }
    };

    // helper: normalize possible handles
    const normalizeHandle = (value) => {
      if (typeof value !== "string") return "";
      return value
        .toLowerCase()
        .trim()
        .replace(/[_\s]+/g, "-")
        .replace(/[^a-z0-9-]/g, "")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "");
    };

    // programmatically activate a step by key (handle, index, or id)
    const activateStepByKey = (rawKey, { retryScroll = false } = {}) => {
      if (!rawKey) return null;
      const key = normalizeHandle(String(rawKey));

      // 1) Try by data-step-handle
      let targetTab = Array.from(tabs).find(
        (t) => normalizeHandle(t.getAttribute("data-step-handle")) === key
      );

      // 2) Try by numeric index (1-based)
      if (!targetTab && /^\d+$/.test(key)) {
        const idx = parseInt(key, 10);
        if (idx >= 1 && idx <= tabs.length) {
          targetTab = tabs[idx - 1];
        }
      }

      // 3) Try by block id
      if (!targetTab) {
        targetTab = Array.from(tabs).find(
          (t) => normalizeHandle(t.getAttribute("data-step-id")) === key
        );
      }

      if (!targetTab) return null;

      const activeId = targetTab.getAttribute("data-step-id");
      const targetContent = section.querySelector(
        `[data-step-id="${activeId}"].steps__content`
      );

      tabs.forEach((tab) => tab.classList.remove("steps__item--active"));
      targetTab.classList.add("steps__item--active");
      if (targetContent) targetContent.classList.add("step-viewed");
      setBulletVariables(targetTab);
      setStickyOffsetFor(targetContent);
      if (targetContent) {
        scrollToStep(targetContent);
        if (retryScroll) scheduleRetryScroll(targetContent);
      }

      return { tab: targetTab, content: targetContent };
    };

    // on load: check URL query ?step=... or hash #step-...
    const activateStepFromUrl = () => {
      try {
        const url = new URL(window.location.href);
        let stepKey = url.searchParams.get("step");
        if (!stepKey && url.hash) {
          stepKey = url.hash.replace(/^#/, "");
        }
        if (!stepKey) {
          // no deep link: ensure defaults
          if (stepsRoot) {
            stepsRoot.classList.remove("steps--deeplink");
            stepsRoot.style.removeProperty("--steps-content-top-pad");
          }
          return;
        }
        stepKey = stepKey.replace(/^step-/, "");
        const activated = activateStepByKey(stepKey, { retryScroll: true });
        if (stepsRoot) {
          stepsRoot.classList.add("steps--deeplink");
          // Force deep-linked content top padding to 9.4rem as requested
          stepsRoot.style.setProperty("--steps-content-top-pad", "9.4rem");
        }
      } catch (e) {
        // no-op
      }
    };

    // initial activation from URL (before scroll listener)
    activateStepFromUrl();
    // ensure alignment after initial layout
    requestAnimationFrame(() => {
      const activeTab = section.querySelector(".steps__item--active");
      if (activeTab) {
        const activeId = activeTab.getAttribute("data-step-id");
        const content = section.querySelector(`[data-step-id="${activeId}"].steps__content`);
        setStickyOffsetFor(content);
      }
    });
    window.addEventListener("resize", () => {
      const activeTab = section.querySelector(".steps__item--active");
      if (!activeTab) return;
      const activeId = activeTab.getAttribute("data-step-id");
      const content = section.querySelector(`[data-step-id="${activeId}"].steps__content`);
      setStickyOffsetFor(content);
    });

    activateStepByScroll();
    window.addEventListener("scroll", activateStepByScroll);
  };

  initSection(document.currentScript.parentElement);

  document.addEventListener("shopify:section:load", function (event) {
    initSection(event.target);
  });
})();
